import React from 'react';
import Layout from './Layout';

const TermsOfService = () => {
  return (
    <Layout>
      <div className="legal-page">
        <div className="legal-hero">
          <h1 className="legal-title">Terms of Service</h1>
          <p className="legal-subtitle">Last Updated: November 16, 2025</p>
        </div>

        <div className="legal-content">
          <section className="legal-section">
            <h2>The Real Deal</h2>
            <p>
              These Terms of Service govern your use of the WestBay Group website and services. 
              We're going to keep this straightforward - no 50-page legal maze. Just the essentials 
              of how we work together. By using our site, you agree to these terms. If you don't agree, 
              that's cool - just don't use our services.
            </p>
          </section>

          <section className="legal-section">
            <h2>1. About WestBay Group</h2>
            <p>
              WestBay Group is a veteran-owned cloud security and DevOps company. We provide:
            </p>
            <ul>
              <li><strong>Poser AI™:</strong> Our patent-pending cloud security compliance platform</li>
              <li><strong>Cloud Architecture:</strong> AWS solutions, DevOps automation, security hardening</li>
              <li><strong>White-Label Services:</strong> Infrastructure for technology partners</li>
              <li><strong>Consulting:</strong> Expert guidance on cloud security and compliance</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>2. Acceptable Use</h2>
            <h3>You Can:</h3>
            <ul>
              <li>Browse our website and blog content</li>
              <li>Contact us for inquiries or consultations</li>
              <li>Subscribe to our newsletter</li>
              <li>Share our content (with attribution)</li>
              <li>Use our services in accordance with your agreement</li>
            </ul>

            <h3>You Can't:</h3>
            <ul>
              <li>❌ Use our services for illegal activities</li>
              <li>❌ Attempt to hack, breach, or otherwise compromise our systems</li>
              <li>❌ Scrape, copy, or redistribute our content without permission</li>
              <li>❌ Impersonate WestBay Group or our employees</li>
              <li>❌ Spam, harass, or abuse our contact forms</li>
              <li>❌ Use our trademarks or branding without authorization</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Intellectual Property</h2>
            <h3>Our Stuff:</h3>
            <p>
              All content on this website - code, design, text, graphics, logos, and the Poser AI™ platform - 
              is owned by WestBay Group and protected by copyright, trademark, and other intellectual property laws.
            </p>
            <ul>
              <li><strong>Poser AI™:</strong> Patent pending. Don't even think about copying it.</li>
              <li><strong>WestBay Group Brand:</strong> Our name, logo, and Van Gogh grunge aesthetic are our trademark</li>
              <li><strong>Blog Content:</strong> Our stories, our words. Attribution required if you share.</li>
            </ul>

            <h3>Your Stuff:</h3>
            <p>
              When you submit content to us (feedback, ideas, suggestions), you grant us a non-exclusive, 
              worldwide, royalty-free license to use it to improve our services. We won't steal your ideas - 
              this is standard legal protection.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Services & Availability</h2>
            <p>
              We work hard to keep our services up and running, but we're not perfect:
            </p>
            <ul>
              <li>Services provided "as is" without warranties</li>
              <li>We may update, modify, or discontinue features without notice</li>
              <li>Scheduled maintenance will be communicated when possible</li>
              <li>We're not liable for service interruptions (but we'll fix them fast)</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. User Accounts</h2>
            <p>
              If you create an account with us:
            </p>
            <ul>
              <li>You're responsible for maintaining account security</li>
              <li>You must provide accurate information</li>
              <li>You can't share your account credentials</li>
              <li>We may suspend or terminate accounts that violate these terms</li>
              <li>You can delete your account at any time</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Payment & Refunds</h2>
            <p>
              For paid services:
            </p>
            <ul>
              <li>Pricing is listed on our website or in your service agreement</li>
              <li>Payment terms are specified in your contract</li>
              <li>Refunds handled on a case-by-case basis</li>
              <li>We reserve the right to change pricing with 30 days notice</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Limitation of Liability</h2>
            <p>
              <strong>The Legal Necessity:</strong> To the maximum extent permitted by law:
            </p>
            <ul>
              <li>WestBay Group is not liable for indirect, incidental, or consequential damages</li>
              <li>Our total liability is limited to the amount you paid us in the past 12 months</li>
              <li>We're not responsible for third-party services or content</li>
            </ul>
            <p>
              <strong>Real Talk:</strong> If something goes wrong because of us, we'll make it right. 
              But we can't be liable for everything under the sun. That's why insurance exists.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Indemnification</h2>
            <p>
              You agree to indemnify and hold WestBay Group harmless from any claims arising from:
            </p>
            <ul>
              <li>Your use of our services</li>
              <li>Your violation of these terms</li>
              <li>Your violation of any laws or third-party rights</li>
            </ul>
            <p>
              Translation: If you do something that gets us sued, you're responsible for it.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Dispute Resolution</h2>
            <p>
              If we have a problem:
            </p>
            <ol>
              <li><strong>Talk to Us First:</strong> Email jim@westbay.io. Most issues can be resolved with a conversation.</li>
              <li><strong>Mediation:</strong> If talking doesn't work, we'll try mediation.</li>
              <li><strong>Arbitration:</strong> If all else fails, disputes will be resolved through binding arbitration.</li>
              <li><strong>Governing Law:</strong> These terms are governed by the laws of [Your State/Country].</li>
            </ol>
          </section>

          <section className="legal-section">
            <h2>10. Demo Mode Disclaimer</h2>
            <p>
              When using our site in "Demo Mode" for investor presentations:
            </p>
            <ul>
              <li>Demo data is stored separately and clearly marked</li>
              <li>Demo submissions are not processed as real inquiries</li>
              <li>Demo mode is for presentation purposes only</li>
              <li>We may delete demo data periodically</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>11. Modifications to Terms</h2>
            <p>
              We may update these Terms of Service from time to time. When we do:
            </p>
            <ul>
              <li>We'll update the "Last Updated" date</li>
              <li>Significant changes will be announced on our website</li>
              <li>Continued use after changes means you accept the new terms</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>12. Termination</h2>
            <p>
              Either party can terminate this agreement:
            </p>
            <ul>
              <li><strong>You:</strong> Stop using our services anytime</li>
              <li><strong>Us:</strong> We may suspend or terminate accounts that violate these terms</li>
              <li>Termination doesn't affect obligations that survive (e.g., payment, liability)</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>13. Contact & Questions</h2>
            <p>
              Questions about these Terms of Service?
            </p>
            <ul>
              <li><strong>Email:</strong> legal@westbay.io</li>
              <li><strong>General Inquiries:</strong> jim@westbay.io</li>
              <li><strong>Mail:</strong> WestBay Group, [Your Address]</li>
            </ul>
          </section>

          <div className="legal-footer">
            <p>
              <strong>Bottom Line:</strong> Be cool. Don't steal our stuff. Don't break the law. 
              If you have questions, ask. We're approachable humans, not a legal department behind 
              20 layers of bureaucracy.
            </p>
            <p>🦅 Built with Philly Grit by WestBay Group • Patent Pending • Veteran-Owned</p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TermsOfService;
